'use client';

import { useEffect, useRef, useCallback, useMemo } from 'react';
import { analytics } from 'libs/segment';
import { ANALYTICS_EVENTS } from '../types';
import type { ScrollDepthProps, TrackingFunction } from '../types';
import { throttle } from '../../../utils/throttle';
import { trackScrollDepth } from '../eventHandlerTracking';

/**
 * Hook for tracking scroll depth events with Segment analytics.
 * Fires events when users scroll to specific percentage thresholds of the page.
 *
 * @param options - Configuration options for scroll depth tracking
 * @returns A function that can be called to manually trigger scroll depth tracking
 *
 * @example
 * ```tsx
 * function MyPage() {
 *   const trackScrollDepth = useScrollDepthTracking();
 *
 *   return <div>Page content</div>;
 * }
 * ```
 */
export const useScrollDepthTracking = (): TrackingFunction => {
  const throttleDelay = 100;

  // Direct analytics tracking function
  const trackSegment = useCallback((properties: ScrollDepthProps) => {
    if (analytics) {
      analytics.track(ANALYTICS_EVENTS.SCROLL_DEPTH, properties);
    }
  }, []);

  const trackedThresholds = useRef<Set<number>>(new Set());
  const isTracking = useRef(false);

  /**
   * Calculates the current scroll percentage of the page
   */
  const getScrollPercent = useCallback((): number => {
    if (typeof window === 'undefined') return 0;

    const h = document.documentElement;
    const b = document.body;
    const st = 'scrollTop';
    const sh = 'scrollHeight';

    const scrollTop = h[st] || b[st];
    const scrollHeight = (h[sh] || b[sh]) - h.clientHeight;

    return Math.ceil((scrollTop / scrollHeight) * 100);
  }, []);

  /**
   * Fires a scroll depth event for the given threshold
   */
  const fireDepthEvent = useCallback(
    (depth: number): void => {
      // Fire Segment event
      trackSegment({
        depth_percentage: depth,
      });

      // Fire event router event
      trackScrollDepth({
        depth_percentage: depth,
      });
    },
    [trackSegment],
  );

  /**
   * Checks current scroll depth and fires events for reached thresholds
   */
  const checkScrollDepth = useCallback((): void => {
    const thresholds = [20, 40, 60, 80, 100];

    if (isTracking.current) return;

    const scrollPercent = getScrollPercent();

    // Check each threshold in ascending order
    for (const threshold of thresholds.sort((a, b) => a - b)) {
      if (scrollPercent >= threshold && !trackedThresholds.current.has(threshold)) {
        trackedThresholds.current.add(threshold);
        fireDepthEvent(threshold);

        // If we've reached 100%, we can stop tracking
        if (threshold === 100) {
          isTracking.current = true;
          break;
        }
      }
    }
  }, [getScrollPercent, fireDepthEvent]);

  // Create throttled version of the scroll depth check
  /* eslint-disable react-hooks/refs -- checkScrollDepth reads refs but is only called via scroll events, not during render */
  const throttledCheckScrollDepth = useMemo(
    () => throttle(() => checkScrollDepth(), throttleDelay),
    [checkScrollDepth, throttleDelay],
  );
  /* eslint-enable react-hooks/refs */

  // Set up scroll event listeners
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Check initial scroll depth
    checkScrollDepth();

    // Add scroll event listener
    window.addEventListener('scroll', throttledCheckScrollDepth, { passive: true });

    return () => {
      window.removeEventListener('scroll', throttledCheckScrollDepth);
    };
  }, [checkScrollDepth, throttledCheckScrollDepth]);

  // Manual trigger function
  const manualTrack = useCallback(
    (_props?: Record<string, unknown>, callback?: () => void) => {
      checkScrollDepth();
      if (callback) callback();
    },
    [checkScrollDepth],
  );

  return manualTrack;
};
